import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Building2, Users, Package, ShoppingCart,
  Settings, ChevronRight, ChevronDown, MapPin, TrendingUp, BookOpen, HelpCircle, Grid3x3, MessageCircle, Hotel, SlidersHorizontal, Activity, Globe2, Search, X, ClipboardList, FileCheck2, ArrowRightLeft, Pencil, ShieldCheck, HardHat
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslations } from '../translations';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Case-insensitive + Turkish-friendly text normalizer.
// ı/i/İ/I all collapse to "i"; ş→s, ç→c, ğ→g, ö→o, ü→u, â/î/û→a/i/u.
const normalizeForSearch = (s) => {
  if (!s) return '';
  const str = String(s);
  // Use locale-aware lowercase first (handles I→i, İ→i in Turkish locale).
  let out = str.toLocaleLowerCase('tr-TR');
  // Replace remaining Turkish-specific letters so they match their ASCII forms.
  const map = { 'ı': 'i', 'ş': 's', 'ç': 'c', 'ğ': 'g', 'ö': 'o', 'ü': 'u',
                'â': 'a', 'î': 'i', 'û': 'u', 'é': 'e', 'è': 'e' };
  out = out.replace(/[ışçğöüâîûéè]/g, (ch) => map[ch] || ch);
  return out.trim();
};

const AdminSidebar = () => {
  const location = useLocation();
  const { language } = useLanguage();
  const t = useTranslations(language);
  const tr = language === 'tr';
  const [expandedMenus, setExpandedMenus] = useState(() => {
    const path = window.location.pathname;
    const defaults = [];
    if (path.startsWith('/ultra-admin/settings') ||
        path === '/ultra-admin/grid-settings' ||
        path === '/ultra-admin/audit-logs') {
      defaults.push('settings');
    }
    // Auto-expand the Stand & Halls group when on any of its children.
    if (path.startsWith('/ultra-admin/blue-zone') ||
        path.startsWith('/ultra-admin/halls')) {
      defaults.push('stand-halls');
    }
    // Auto-expand the Catalogs group when on any of its children.
    // (`platform-products` is now a TOP-LEVEL item, no longer needs to expand
    // the Catalogs group.)
    if (path.startsWith('/ultra-admin/modular-packages') ||
        path.startsWith('/ultra-admin/service-catalog') ||
        path.startsWith('/ultra-admin/organizer-products') ||
        path.startsWith('/ultra-admin/supplier-products') ||
        path.startsWith('/ultra-admin/organizer-categories')) {
      defaults.push('catalogs');
    }
    return defaults;
  });
  const [unreadCount, setUnreadCount] = useState(0);
  const [pendingAppsCount, setPendingAppsCount] = useState(0);
  // Phase 86P — additional sidebar counts (stand requests, change requests, stand designs).
  // All four come from a single /sidebar-counts call to avoid 4× polling overhead.
  const [standRequestsCount, setStandRequestsCount] = useState(0);
  const [changeRequestsCount, setChangeRequestsCount] = useState(0);
  const [standDesignsCount, setStandDesignsCount] = useState(0);
  // Phase 86AZ.59 — Manual-onboarding pending activations (supplier + contractor).
  const [pendingSuppliersCount,  setPendingSuppliersCount]  = useState(0);
  const [pendingContractorsCount, setPendingContractorsCount] = useState(0);
  const [searchQ, setSearchQ] = useState('');

  // Poll unread count
  useEffect(() => {
    const token = localStorage.getItem('cop31_ultra_token');
    if (!token) return;
    const fetchUnread = async () => {
      try {
        const res = await axios.get(`${API}/chat/admin-unread-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUnreadCount(res.data.count || 0);
      } catch {}
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 5000);
    return () => clearInterval(id);
  }, []);

  // Phase 86P — Single aggregate poll for all pending counts the sidebar
  // needs to badge. Drops the 8s app-only polling above with a 6s combined
  // fetch covering applications + stand requests + change requests + stand
  // designs. Backend endpoint: GET /ultra-admin/sidebar-counts.
  useEffect(() => {
    const token = localStorage.getItem('cop31_ultra_token');
    if (!token) return;
    const fetchPendingApps = async () => {
      try {
        const res = await axios.get(`${API}/ultra-admin/sidebar-counts`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPendingAppsCount(res.data.applications || 0);
        setStandRequestsCount(res.data.stand_requests || 0);
        setChangeRequestsCount(res.data.change_requests || 0);
        setStandDesignsCount(res.data.stand_designs || 0);
        setPendingSuppliersCount(res.data.pending_suppliers   || 0);
        setPendingContractorsCount(res.data.pending_contractors || 0);
      } catch {}
    };
    fetchPendingApps();
    const id = setInterval(fetchPendingApps, 6000);
    return () => clearInterval(id);
  }, []);

  const toggleMenu = (menuId) => {
    setExpandedMenus(prev => 
      prev.includes(menuId) 
        ? prev.filter(id => id !== menuId)
        : [...prev, menuId]
    );
  };

  const menuItems = [
    // ── Daily operation ──────────────────────────────────────────────
    {
      id: 'dashboard',
      labelKey: 'dashboard',
      icon: LayoutDashboard,
      path: '/ultra-admin/dashboard',
      submenu: []
    },
    // Phase 86S — Başvurular menüsü Panel'in hemen altına taşındı (kullanıcı
    // talebi). Onboarding aşamasındaki müşterileri en hızlı erişilen konumda
    // tutar.
    {
      id: 'applications',
      label: tr ? 'Başvurular' : 'Applications',
      icon: FileCheck2,
      path: '/ultra-admin/dashboard/pending',
      submenu: [],
      badge: pendingAppsCount
    },
    {
      id: 'tenants',
      labelKey: 'tenant_management',
      icon: Building2,
      path: '/ultra-admin/tenants',
      submenu: []
    },
    {
      id: 'users',
      labelKey: 'user_management',
      icon: Users,
      path: '/ultra-admin/users',
      submenu: [],
      // Phase 86AZ.59 — Combined "pending activation" badge for manually-created
      // suppliers + contractors who haven't set their password yet.
      badge: pendingSuppliersCount + pendingContractorsCount,
    },
    {
      id: 'stand-requests',
      label: tr ? 'Stand Talepleri' : 'Stand Requests',
      icon: Package,
      path: '/ultra-admin/stand-requests',
      submenu: [],
      badge: standRequestsCount
    },
    // ── Phase 82: Dashboard tab'larından yan menüye taşınanlar ───────
    // Bu iki giriş UltraAdminDashboard içindeki sekmelere deep-link veriyor.
    // Tab triggers dashboard başlığından kaldırıldığı için kullanıcı bu
    // akışlara artık yalnızca sidebar üzerinden ulaşıyor.
    {
      id: 'change-requests',
      label: tr ? 'Değişiklik Talepleri' : 'Change Requests',
      icon: ArrowRightLeft,
      path: '/ultra-admin/dashboard/stand-requests',
      submenu: [],
      badge: changeRequestsCount
    },
    {
      id: 'stand-designs',
      label: tr ? 'Stand Tasarımları' : 'Stand Designs',
      icon: Pencil,
      path: '/ultra-admin/dashboard/stand-designs',
      submenu: [],
      badge: standDesignsCount
    },
    {
      // Phase 86AZ.57 — ISG (Health & Safety) module
      id: 'isg',
      label: tr ? 'ISG' : 'ISG',
      icon: ShieldCheck,
      path: '/ultra-admin/dashboard/isg',
      submenu: [],
    },
    // Phase 86AZ.60 / Feb 2026 — Tedarikçi Aday Öner module (renamed from
    // müteahhit aday öner). Contractor role itself is unaffected.
    {
      id: 'tedarikci-aday-oner',
      label: tr ? 'Tedarikçi Aday Öner' : 'Supplier Recommender',
      icon: HardHat,
      path: '/ultra-admin/tedarikci/adaylar',
      submenu: [
        { id: 'tedarikci-adaylar',    label: tr ? 'Adaylar'    : 'Candidates',   path: '/ultra-admin/tedarikci/adaylar'    },
        { id: 'tedarikci-onericiler', label: tr ? 'Önericiler' : 'Recommenders', path: '/ultra-admin/tedarikci/onericiler' },
      ],
    },
    {
      id: 'orders',
      label: tr ? 'Siparişler' : 'Orders',
      icon: ClipboardList,
      // Default landing for the Orders hub. The page itself renders a 2nd
      // column (OrdersSubSidebar) exposing General Orders & Service Orders.
      path: '/ultra-admin/orders',
      submenu: []
    },
    {
      id: 'sales',
      label: tr ? 'Alışveriş Raporları' : 'Sales Reports',
      icon: TrendingUp,
      path: '/ultra-admin/sales',
      submenu: []
    },
    {
      id: 'messages',
      label: tr ? 'Mesajlar' : 'Messages',
      icon: MessageCircle,
      path: '/ultra-admin/messages',
      submenu: [],
      badge: unreadCount
    },
    // ── Stand & Halls group (expandable) ─────────────────────────────
    {
      id: 'stand-halls',
      label: tr ? 'Stand & Hall' : 'Stand & Halls',
      icon: Hotel,
      // No top-level path — clicking the header toggles the submenu.
      submenu: [
        { id: 'stand-layouts', label: tr ? 'Stand Layoutları' : 'Stand Layouts', path: '/ultra-admin/blue-zone' },
        { id: 'halls',         label: tr ? 'Hall Yönetimi'   : 'Hall Management', path: '/ultra-admin/halls' },
      ]
    },
    // ── Modular Stands (top-level — promoted out of Catalogs for visibility) ─
    {
      id: 'platform-products',
      label: tr ? 'Modüler Standlar' : 'Modular Stands',
      icon: Package,
      path: '/ultra-admin/platform-products',
      submenu: []
    },
    // ── Catalogs group (expandable) ───────────────────────────────────
    {
      id: 'catalogs',
      label: tr ? 'Kataloglar' : 'Catalogs',
      icon: Package,
      submenu: [
        { id: 'modular-packages',      label: tr ? 'Modüler Paketler'    : 'Modular Packages',    path: '/ultra-admin/modular-packages' },
        { id: 'service-catalog',       label: tr ? 'Servis Kataloğu'     : 'Service Catalog',     path: '/ultra-admin/service-catalog' },
        { id: 'organizer-products',    label: tr ? 'Organizatör Ürünleri': 'Organizer Products',  path: '/ultra-admin/organizer-products' },
        { id: 'supplier-products',     label: tr ? 'Tedarikçi Ürünleri'  : 'Supplier Products',   path: '/ultra-admin/supplier-products' },
        { id: 'organizer-categories',  label: tr ? 'Ürün Kategorileri'   : 'Product Categories',  path: '/ultra-admin/organizer-categories' },
      ]
    },
    // ── System Settings (last, with direct access to config sub-pages) ─
    // Note: "Super Admins" management now lives INSIDE System Settings as a
    // shortcut section (gated by is_main_user in UltraAdminSettings.jsx) —
    // removed from the top-level sidebar to keep it clean.
    {
      id: 'settings',
      labelKey: 'system_settings',
      icon: Settings,
      path: '/ultra-admin/settings',
      submenu: []
    },
  ];

  // ── Apply search filter (case-insensitive + Turkish-friendly) ──
  // Normalized query; empty = show everything. Matches main item labels AND
  // submenu labels. When only submenu matches, the parent is auto-expanded
  // and its submenu is filtered to the matching rows only.
  const nq = normalizeForSearch(searchQ);
  const { filteredMenu, forceExpandedIds } = useMemo(() => {
    if (!nq) return { filteredMenu: menuItems, forceExpandedIds: [] };
    const forceIds = [];
    const out = [];
    menuItems.forEach((item) => {
      const label = item.label || (item.labelKey ? t(item.labelKey) : item.id);
      const mainHit = normalizeForSearch(label).includes(nq);
      const sub = Array.isArray(item.submenu) ? item.submenu : [];
      const subHits = sub.filter((si) => {
        const sl = si.label || (si.labelKey ? t(si.labelKey) : '');
        return normalizeForSearch(sl).includes(nq);
      });
      if (mainHit) {
        // Include whole item; if it has submenu, still show all sub rows.
        out.push(item);
      } else if (subHits.length > 0) {
        out.push({ ...item, submenu: subHits });
        forceIds.push(item.id);
      }
    });
    return { filteredMenu: out, forceExpandedIds: forceIds };
  }, [nq, language, pendingAppsCount, standRequestsCount, changeRequestsCount, standDesignsCount, unreadCount]);  // Phase 86P — include badge counts so memo invalidates when they change

  const effectiveExpanded = nq ? Array.from(new Set([...expandedMenus, ...forceExpandedIds])) : expandedMenus;

  return (
    <div className="w-64 bg-white border-r border-gray-200 h-screen sticky top-0 overflow-y-auto">
      <div className="p-6">
        <div className="flex items-center gap-3 mb-8">
          <div className="h-10 w-10 rounded-full bg-purple-700 flex items-center justify-center">
            <LayoutDashboard className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="font-bold text-gray-900">Ultra Admin</h2>
            <p className="text-xs text-gray-500">{t('control_center')}</p>
          </div>
        </div>

        <nav className="space-y-1">
          {/* Search — filters main menu + submenu (case-insensitive, TR-aware) */}
          <div className="relative mb-3">
            <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              placeholder={tr ? 'Menüde ara...' : 'Search menu...'}
              data-testid="admin-sidebar-search"
              className="w-full text-sm pl-9 pr-8 py-2 rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-purple-200 focus:border-purple-400 outline-none transition-colors"
            />
            {searchQ && (
              <button
                type="button"
                onClick={() => setSearchQ('')}
                data-testid="admin-sidebar-search-clear"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                aria-label={tr ? 'Temizle' : 'Clear'}
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {filteredMenu.length === 0 && (
            <div className="text-xs text-gray-400 text-center py-4" data-testid="admin-sidebar-no-results">
              {tr ? 'Sonuç bulunamadı' : 'No results'}
            </div>
          )}

          {filteredMenu.map((item) => {
            const Icon = item.icon;
            const isExpanded = effectiveExpanded.includes(item.id);
            const hasSubmenu = item.submenu && item.submenu.length > 0;
            const isActive = item.path
              ? (item.path === '/ultra-admin/blue-zone' || item.path === '/ultra-admin/halls' || item.path === '/ultra-admin/platform-products')
                ? location.pathname.startsWith(item.path)
                : location.pathname === item.path
              : false;
            const isSettingsGroupActive = item.id === 'settings' &&
              (location.pathname.startsWith('/ultra-admin/settings') ||
               location.pathname === '/ultra-admin/grid-settings' ||
               location.pathname === '/ultra-admin/audit-logs');
            // Orders is a hub: highlight whether on /orders or /service-orders
            // (mirrors the System Settings group-active pattern).
            const isOrdersGroupActive = item.id === 'orders' &&
              (location.pathname.startsWith('/ultra-admin/orders') ||
               location.pathname.startsWith('/ultra-admin/service-orders'));
            // Stand & Halls group: highlight when any child is active.
            const isStandHallsGroupActive = item.id === 'stand-halls' &&
              (location.pathname.startsWith('/ultra-admin/blue-zone') ||
               location.pathname.startsWith('/ultra-admin/halls'));
            // Catalogs group: highlight when any child is active.
            const isCatalogsGroupActive = item.id === 'catalogs' &&
              (location.pathname.startsWith('/ultra-admin/platform-products') ||
               location.pathname.startsWith('/ultra-admin/modular-packages') ||
               location.pathname.startsWith('/ultra-admin/service-catalog') ||
               location.pathname.startsWith('/ultra-admin/organizer-products') ||
               location.pathname.startsWith('/ultra-admin/supplier-products') ||
               location.pathname.startsWith('/ultra-admin/organizer-categories'));
            const isAnyGroupActive = isSettingsGroupActive || isStandHallsGroupActive || isCatalogsGroupActive;
            const label = item.label || (item.labelKey ? t(item.labelKey) : item.id);
            const isSalesActive = location.pathname.startsWith('/ultra-admin/sales');

            return (
              <div key={item.id}>
                {hasSubmenu ? (
                  item.path ? (
                    // Legacy pattern: header is clickable (goes to item.path),
                    // separate chevron toggles the submenu. Used by `settings`.
                    <div className="w-full flex items-stretch rounded-lg group overflow-hidden">
                      <Link
                        to={item.path}
                        className={`flex-1 flex items-center gap-3 px-3 py-2.5 transition-colors ${
                          (item.id === 'sales' && isSalesActive) || isAnyGroupActive
                            ? 'bg-purple-100 text-purple-700'
                            : 'text-gray-700 hover:bg-purple-50 hover:text-purple-700'
                        }`}
                      >
                        <Icon className="h-5 w-5" />
                        <span className="font-medium text-sm">{label}</span>
                      </Link>
                      <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleMenu(item.id); }}
                        aria-label="Toggle submenu"
                        className={`px-2 transition-colors ${
                          (item.id === 'sales' && isSalesActive) || isAnyGroupActive
                            ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                            : 'text-gray-500 hover:bg-purple-50 hover:text-purple-700'
                        }`}
                      >
                        {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </button>
                    </div>
                  ) : (
                    // Pure group header (no path) — whole row toggles submenu.
                    // Used by `stand-halls` and `catalogs`.
                    <button
                      onClick={() => toggleMenu(item.id)}
                      aria-expanded={isExpanded}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                        isAnyGroupActive
                          ? 'bg-purple-100 text-purple-700'
                          : 'text-gray-700 hover:bg-purple-50 hover:text-purple-700'
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                      <span className="font-medium text-sm flex-1 text-left">{label}</span>
                      {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </button>
                  )
                ) : (
                  <Link
                    to={item.path}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                      isActive || isOrdersGroupActive
                        ? 'bg-purple-100 text-purple-700'
                        : 'text-gray-700 hover:bg-purple-50 hover:text-purple-700'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="font-medium text-sm flex-1">{label}</span>
                    {item.badge > 0 && (
                      <span className="bg-purple-600 text-white text-xs px-1.5 py-0.5 rounded-full font-bold min-w-[18px] text-center">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                )}

                {hasSubmenu && isExpanded && (
                  <div className="ml-8 mt-1 space-y-1">
                    {item.submenu.map((subitem, idx) => {
                      const SubIcon = subitem.icon;
                      const subPath = subitem.path.split('?')[0];
                      const subTab = new URLSearchParams(subitem.path.split('?')[1] || '').get('tab');
                      const currentTab = new URLSearchParams(location.search).get('tab');
                      const isSubActive = location.pathname === subPath && (!subTab || currentTab === subTab);
                      return (
                        <Link
                          key={idx}
                          to={subitem.path}
                          className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${
                            isSubActive
                              ? 'bg-purple-50 text-purple-700 font-medium'
                              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          }`}
                        >
                          {SubIcon && <SubIcon className="h-3.5 w-3.5" />}
                          {subitem.label || t(subitem.labelKey)}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </div>
    </div>
  );
};

export default AdminSidebar;